import { useCallback, useMemo, useState } from "react";
import {
  SCROLL_END_GUTTER,
  ALL_GROUP_KEY,
  Box,
  DataTable,
  DataTableCellContent,
  DataTableCellTrigger,
  Text,
  WidgetEmpty,
  WidgetEmptyBands,
  createColumnHelper,
  useWidgetSurface,
  type DataTableColumnDef,
  type DataTableGroupType,
  type WidgetEmptyBand,
} from "@salestrak/ui";
import {
  GroupQueryProvider,
  LocalGroupProvider,
  useGroupStates,
} from "#app/lib/hooks/group-query-provider";
import { ISSUE_TYPE_COLUMN_ID, useIssueColumns } from "#app/components/issues/issue-columns";
import {
  AGE_GROUPS,
  UNASSIGNED_DIMENSION_LABEL,
  UNASSIGNED_GROUP_KEY,
  type IssueGroupDimension,
  type IssueTableGroupBy,
} from "#app/components/issues/issue-grouping";
import { IssueGroupTitle } from "#app/components/issues/issue-group-title";
import { useIssueBulkActions } from "#app/components/issues/use-issue-bulk-actions";
import { useIssueView } from "#app/components/issue-view";
import { UpdateAgeDialog } from "#app/components/age/update-age-dialog";
import { useIssueQueryGroups } from "#app/hooks/issue-list/use-issue-query-groups";
import { useIssueListMutations } from "#app/hooks/issue-list/use-issue-list-mutations";
import { useRefetchIssueGroups } from "#app/hooks/issue-list/use-refetch-issue-groups";
import { useDashboardScope } from "#app/widgets/dashboards/scope/dashboard-scope-context";
import { AnalyticsScope, AnalyticsView } from "#app/lib/analytics";
import { scopeIsWorkflowBound } from "#app/lib/filters/issue-scope";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { type IssueRow, IssuesDocument, type SortDirection, type StageOption } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// The bulk bar's count-chip noun: "1 Project selected".
const PROJECT_SELECTION_NOUN = { one: "Project", other: "Projects" };

export type { IssueTableGroupBy };

/**
 * `IssueTable` — the shared presentational table every issue-listing card renders:
 * the real library `DataTable` driven by the SHARED issue columns (`useIssueColumns`),
 * so each cell routes through `#app/components/entity` and the rows stay live (open, change
 * stage, edit assignees, age dialog) — never a hand-rolled table.
 *
 * It owns everything downstream of the rows: stage resolution, the open/stage/assignee
 * handlers, the age dialog, the scope-aware bulk actions, and the width-driven column
 * set. Two source modes, one paint path:
 *
 * - **Query mode** (`query`, used by `entity-list`): every group is its own windowed
 *   server query (`GroupQueryProvider` over the shared `Issues` document + the shared
 *   `buildGroupWhere` selectors — the exact machinery behind the full-page tables).
 *   The table reaches EVERY row in scope through the DataTable's windowed scroll;
 *   nothing is ever a capped bulk chunk. Grouped mode resolves its group set from
 *   the exact server `issueGroupCounts` aggregation.
 * - **Local mode** (`rows`, used by `favorites-list`): a fully-materialized bounded
 *   set (the starred issues) pages through `LocalGroupProvider`, the local twin.
 *
 * Bulk actions read the ambient `DashboardScope`: move-to-stage only under a
 * single-workflow dashboard (a cross-workflow/unscoped selection has no single stage
 * target — so it's absent on `favorites-list`); assignees for any scope.
 */
export type IssueTableQuery = {
  /** The fully-folded base `where` — scope + card Filters + search. The table
   *  derives every group's predicate from it via `buildGroupWhere`. */
  where: Record<string, unknown> | undefined;
  /** Server ordering for every group's paged fetch. */
  orderBy: Record<string, SortDirection>;
  /**
   * The row count the caller already holds — the figure whose rows this table
   * discloses — so the table paints a sized skeleton under its column header
   * while the rows load, and a zero shows the empty state without waiting. A
   * fresh query has no count of its own until it answers, and with none the
   * header yields to a blank until the rows or the empty state settle; on a
   * table that was already showing the previous slice that reads as a flash.
   * Ungrouped query mode only — a grouped table sizes each group from its own
   * aggregation.
   */
  total?: number | undefined;
};

/**
 * A per-row figure the caller aggregated, shown as the column right after the
 * name. For a table whose figure counts EVENTS rather than rows — the stat
 * strip's Updates drill lists the projects its Updates landed on and how many
 * landed on each — so the column sums to the figure the way a project count's
 * rows count up to it. It sits beside the name because it is what the rows
 * disclose: the name says which project, the figure says why it is listed,
 * and the entity columns after them are context. The cell is a press target
 * like the Follow-up cell beside it, and it opens the project on
 * its Activity tab — where the events the figure counts are listed. Keyed by
 * issue id; a row without an entry shows an empty cell, not a zero, because
 * the caller's row set is the rows that HAVE one.
 */
export type IssueTableCountColumn = {
  header: string;
  counts: Record<string, number>;
};

export type IssueTableProps = Omittable<{
  /** Query mode: windowed per-group server paging over the whole scoped set. */
  query?: IssueTableQuery;
  /** Local mode: the fully-materialized set (favorites). Ignored when `query` is set. */
  rows?: IssueRow[];
  /** Local-mode initial loading; query mode derives its own. */
  loading?: boolean;
  /** Local mode only: semantic identity of `rows` (query variables + client
   *  narrowing). Flips reset each group's scroll window. */
  variablesKey?: string;
  /** Live span width in grid columns — narrow cards drop to the name alone. */
  w: number;
  /** Refetch used by the move-to-stage bulk commit. Query mode defaults to
   *  refetching the shared issue documents; local readers may omit it. */
  refetch?: () => Promise<unknown>;
  emptyTitle: string;
  emptyDescription?: string;
  /** The empty state's one door, forwarded to `WidgetEmpty` — a `WidgetEmptyAction`. */
  emptyAction?: React.ReactNode;
  /** The bands an empty table stands with zeros (`WidgetEmptyBands`) in place
   *  of the title-only empty — the same bands its list twin stands. */
  emptyBands?: readonly WidgetEmptyBand[];
  emptyNote?: string;
  /** Header label for the single (all-rows) group. */
  groupLabel?: string;
  groupBy?: IssueTableGroupBy;
  /** Personal-home treatment: the inventory column set (`HOME_COLUMN_IDS`)
   *  and no bulk-selection chrome. */
  home?: boolean;
  /** Row selection + the bulk actions bar. Off for a table whose rows are a
   *  disclosure of a number rather than a work surface — the stat strip's
   *  inline peek — where a checkbox column reads as an invitation to act on
   *  the set that the card never made. `home` implies off. */
  selectable?: boolean;
  /** A per-row figure, seated after the name in the width-driven column set —
   *  see {@link IssueTableCountColumn}. */
  countColumn?: IssueTableCountColumn;
}>;

/** Columns the card keeps at a given width — name always leads (it fills),
 *  and it carries the stage as its leading glyph, so no width needs a stage
 *  column. */
const WIDE_COLUMN_IDS = new Set(["name", "assignees", "age"]);
const NARROW_COLUMN_IDS = new Set(["name"]);
/** The personal-home INVENTORY set (My Work's Assigned to me): what the reader
 *  owns — name, type, assignees. The stage rides the name cell as its leading
 *  glyph, as at every other width, so no Stage column: named beside the glyph
 *  it is the same fact twice in one row. No Follow up column: the board's
 *  queue card owns that fact, and two cards showing the same rows with the
 *  same chip read as one card printed twice. */
const HOME_COLUMN_IDS = new Set(["name", ISSUE_TYPE_COLUMN_ID, "assignees"]);

const h = createColumnHelper<IssueRow>();

/** Relay-connection accessor for the shared `Issues` document. */
const getIssuesConnection = (data: any) => data?.issues;
const EMPTY_ISSUES: IssueRow[] = [];
const PAGE_LIMIT = 50;

/** A partitioned slice of the card's rows — the shape of one table group. */
type IssueBucket = { key: string; label: string; data: IssueRow[] };

function makeBucket(key: string, label: string, data: IssueRow[]): IssueBucket {
  return { key, label, data };
}

function partitionByKey(rows: IssueRow[], getKey: (row: IssueRow) => string) {
  const buckets = new Map<string, IssueRow[]>();
  for (const row of rows) {
    const key = getKey(row);
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }
  return buckets;
}

export function IssueTable({
  query,
  rows,
  loading = false,
  variablesKey = "",
  w,
  refetch,
  emptyTitle,
  emptyDescription,
  emptyAction,
  emptyBands,
  emptyNote,
  groupLabel = "Projects",
  groupBy = "none",
  home = false,
  selectable = true,
  countColumn,
}: IssueTableProps) {
  const selectionOn = selectable && !home;
  const isQueryMode = query != null;
  const localRows = rows ?? EMPTY_ISSUES;
  // The surface the enclosing widget card painted — the table's opaque parts
  // must match it (an elevated tile and a page-flow card fill differently in
  // dark mode).
  const widgetSurface = useWidgetSurface();

  // Stage labels resolve from every workspace stage — the scope can span
  // workflows, so the name cell's stage glyph always resolves its stage.
  const { stages: workspaceStages, workflowMap, issueTypeMap } = useWorkspaceData();
  // `useWorkspaceData()` stages ARE the stage row — no re-shaping.
  const availableStages: StageOption[] = workspaceStages;
  const stageById = useMemo(() => {
    const map = new Map<string, StageOption>();
    for (const stage of availableStages) map.set(stage.stageId, stage);
    return map;
  }, [availableStages]);

  // Live row cells — the same handlers as the list view.
  const { openIssue } = useIssueView();
  const handleOpenIssue = useCallback(
    (issue: IssueRow) => openIssue(issue, { source: "widget" }),
    [openIssue],
  );
  // The figure column's press: the events it counts live on the Activity tab.
  const handleOpenActivity = useCallback(
    (issue: IssueRow) => openIssue(issue, { initialTab: "activity", source: "widget" }),
    [openIssue],
  );
  const { changeStage, updateAssignees } = useIssueListMutations();
  const [ageDialogIssue, setAgeDialogIssue] = useState<IssueRow | null>(null);
  const handleAgePress = useCallback((issue: IssueRow) => setAgeDialogIssue(issue), []);

  // Bulk actions mirror the list view (and the widget drilldown): move-to-stage
  // only under a single-workflow dashboard (a cross-workflow selection has no
  // single stage target); assignees for any scope. Passing `bulkActions` is
  // what surfaces the row-hover checkbox + select-all + the bulk actions bar.
  // The card loads its whole window client-side, so commits run on selected ids.
  const scope = useDashboardScope();
  const analyticsScope =
    scope?.entity && scopeIsWorkflowBound(scope.entity.type)
      ? AnalyticsScope.Workflow
      : scope?.entity?.type === "team"
        ? AnalyticsScope.Team
        : AnalyticsScope.Member;
  const scopeWorkflowId = scope?.entity?.type === "workflow" ? scope.entity.id : undefined;
  const refetchIssueGroups = useRefetchIssueGroups();
  const refetchGroups = useMemo(
    () => refetch ?? (isQueryMode ? refetchIssueGroups : async () => {}),
    [refetch, isQueryMode, refetchIssueGroups],
  );
  // The shared project bulk roster: Stage only under a single-workflow
  // dashboard; commits run on the selected ids (the card loads its whole
  // window client-side).
  const bulkActions = useIssueBulkActions({
    view: AnalyticsView.WidgetView,
    scope: analyticsScope,
    workflowId: scopeWorkflowId,
    availableStages,
    refetchGroups,
  });

  const rawColumns = useIssueColumns({
    availableStages,
    onOpenIssue: handleOpenIssue,
    onChangeStage: changeStage,
    onAssigneesChange: updateAssignees,
    onAgePress: handleAgePress,
  });
  const visibleIds = home ? HOME_COLUMN_IDS : w < 4 ? NARROW_COLUMN_IDS : WIDE_COLUMN_IDS;
  // The figure column keys on its two fields, not the object: a caller builds
  // the descriptor inline on every render around a memoized count map.
  const countHeader = countColumn?.header;
  const counts = countColumn?.counts;
  const columns = useMemo<DataTableColumnDef<IssueRow>[]>(() => {
    const kept = rawColumns.filter((c) => {
      const id = c.id ?? (c as { accessorKey?: string }).accessorKey;
      return id != null && visibleIds.has(String(id));
    });
    if (countHeader == null || !counts) return kept;
    // Right after the name, and kept at every width: the figure is what the
    // caller's rows disclose, so a narrow card keeps it over a second entity
    // column. The cell wears the same trigger shell as the Follow-up
    // cell — the outline is the affordance, and the press opens the project's
    // Activity tab. Start-aligned under a start-aligned header, not a numeric
    // column: one figure per row has no column of digits to line up, and a
    // right-aligned header over a left-aligned cell reads as a mistake. The
    // text is the default cell's — `size={null}` lets the row's density size
    // inherit — so the figure sits on the same baseline as the name.
    const figure = h.display({
      id: "count",
      header: countHeader,
      meta: { width: 100, minWidth: 80, maxWidth: 160 },
      cell: ({ row }) => {
        const count = counts[row.original.issueId];
        return count == null ? null : (
          <DataTableCellTrigger
            onClick={() => handleOpenActivity(row.original)}
            aria-label={`Open activity for ${row.original.name}`}
          >
            <DataTableCellContent>
              <Text size={null} truncate>
                {count.toLocaleString()}
              </Text>
            </DataTableCellContent>
          </DataTableCellTrigger>
        );
      },
    });
    // The name always leads (every column set starts with it), so the figure
    // lands at index 1; the guard keeps the column from vanishing if a set
    // ever omits the name.
    const nameIndex = kept.findIndex(
      (c) => (c.id ?? (c as { accessorKey?: string }).accessorKey) === "name",
    );
    const at = nameIndex === -1 ? 0 : nameIndex + 1;
    return [...kept.slice(0, at), figure, ...kept.slice(at)];
  }, [rawColumns, visibleIds, countHeader, counts, handleOpenActivity]);

  const buckets = useMemo<IssueBucket[]>(() => {
    if (isQueryMode) return [];
    const rows = localRows;
    if (groupBy === "none") {
      return [makeBucket(ALL_GROUP_KEY, groupLabel, rows)];
    }

    if (groupBy === "stage") {
      const byStage = partitionByKey(rows, (row) => row.stageId || UNASSIGNED_GROUP_KEY);
      const assigned = Array.from(byStage.entries())
        .filter(([key]) => key !== UNASSIGNED_GROUP_KEY)
        .sort((a, b) => {
          const stageA = stageById.get(a[0]);
          const stageB = stageById.get(b[0]);
          return (stageA?.sortOrder ?? Number.MAX_SAFE_INTEGER) - (stageB?.sortOrder ?? Number.MAX_SAFE_INTEGER);
        })
        .map(([key, data]) => makeBucket(key, stageById.get(key)?.name ?? key, data));
      const unassigned = byStage.get(UNASSIGNED_GROUP_KEY);
      return unassigned
        ? [...assigned, makeBucket(UNASSIGNED_GROUP_KEY, UNASSIGNED_DIMENSION_LABEL.stage, unassigned)]
        : assigned;
    }

    if (groupBy === "workflow") {
      const byWorkflow = partitionByKey(rows, (row) => row.workflowId || UNASSIGNED_GROUP_KEY);
      const named = Array.from(byWorkflow.entries())
        .filter(([key]) => key !== UNASSIGNED_GROUP_KEY)
        .map(([key, data]) => makeBucket(key, workflowMap.get(key)?.name ?? key, data))
        .sort((a, b) => a.label.localeCompare(b.label));
      const unassigned = byWorkflow.get(UNASSIGNED_GROUP_KEY);
      return unassigned
        ? [...named, makeBucket(UNASSIGNED_GROUP_KEY, UNASSIGNED_DIMENSION_LABEL.workflow, unassigned)]
        : named;
    }

    if (groupBy === "age") {
      const byAge = partitionByKey(rows, (row) => row.age?.status ?? UNASSIGNED_GROUP_KEY);
      return AGE_GROUPS.map(({ key, label }) => {
        const data = byAge.get(key);
        return data ? makeBucket(key, label, data) : null;
      }).filter((bucket): bucket is IssueBucket => bucket != null);
    }

    const byType = partitionByKey(rows, (row) => row.issueTypeId ?? UNASSIGNED_GROUP_KEY);
    const named = Array.from(byType.entries())
      .filter(([key]) => key !== UNASSIGNED_GROUP_KEY)
      .map(([key, data]) => {
        const issueTypeName = data[0]?.issueType?.name ?? issueTypeMap.get(key)?.name ?? key;
        return makeBucket(key, issueTypeName, data);
      })
      .sort((a, b) => a.label.localeCompare(b.label));
    const unassigned = byType.get(UNASSIGNED_GROUP_KEY);
    return unassigned
      ? [...named, makeBucket(UNASSIGNED_GROUP_KEY, UNASSIGNED_DIMENSION_LABEL.issueType, unassigned)]
      : named;
  }, [isQueryMode, localRows, groupBy, groupLabel, stageById, workflowMap, issueTypeMap]);

  // Every group pages through its own provider (mounted below); the table
  // groups read the reported windows, mirroring the full-page tables.
  const { states, registerState, getGroupProps } = useGroupStates<IssueRow>();
  const groupContext = `${variablesKey}|${groupBy}`;

  // Query mode, grouped: the group SET comes from the exact server aggregation
  // (`issueGroupCounts`) over the same folded `where` — never from a loaded
  // sample. Each non-empty group then gets its own windowed query.
  const queryDimension: IssueGroupDimension | null =
    isQueryMode && groupBy !== "none" ? groupBy : null;
  const { groups: queryGroupDefs, ready: queryGroupsReady } = useIssueQueryGroups({
    where: query?.where,
    dimension: queryDimension,
  });

  const groups = useMemo<DataTableGroupType<IssueRow>[]>(() => {
    if (isQueryMode) {
      if (queryGroupDefs) {
        return queryGroupDefs.map((def) => ({
          key: def.key,
          label: def.label,
          totalCount: states.get(def.key)?.total ?? def.totalCount,
          ...getGroupProps(def.key),
        }));
      }
      const allState = states.get(ALL_GROUP_KEY);
      return [
        {
          key: ALL_GROUP_KEY,
          label: groupLabel,
          // The group's own total once it has answered. While it loads it
          // holds zero, so the caller's count stands in: the header stays put
          // and the rows' space is reserved under it.
          totalCount: allState && !allState.loading ? allState.total : query.total,
          ...getGroupProps(ALL_GROUP_KEY),
        },
      ];
    }
    return buckets.map((bucket) => ({
      key: bucket.key,
      label: bucket.label,
      totalCount: states.get(bucket.key)?.total,
      ...getGroupProps(bucket.key),
    }));
  }, [isQueryMode, queryGroupDefs, groupLabel, buckets, states, getGroupProps, query?.total]);

  // Age groups keep their plain-text labels (the table's own label rendering);
  // the entity dimensions render the group's chip through the shared title.
  const groupTitleDimension =
    groupBy === "stage" || groupBy === "workflow" || groupBy === "issueType" ? groupBy : null;
  const renderGroupTitle = useCallback(
    (groupKey: string) =>
      groupTitleDimension ? (
        <IssueGroupTitle dimension={groupTitleDimension} groupKey={groupKey} />
      ) : (
        groupKey
      ),
    [groupTitleDimension],
  );

  // Query mode derives its own loading/empty from the group states + counts;
  // local mode keeps the caller's `loading` over the materialized rows.
  const allState = states.get(ALL_GROUP_KEY);
  const tableLoading = isQueryMode
    ? queryDimension
      ? !queryGroupsReady
      : (allState?.loading ?? true)
    : loading;
  const isEmpty = isQueryMode
    ? queryDimension
      ? queryGroupsReady && (queryGroupDefs?.length ?? 0) === 0
      : (allState != null && !allState.loading && allState.total === 0) ||
        // A count the caller knows to be zero is empty NOW, not after the round
        // trip that confirms it: a figure of 0 opening a run of placeholder rows
        // promises rows that are not coming. An answer that disagrees — rows
        // created between the count and the drill — wins the moment it lands.
        (query.total === 0 && (allState == null || allState.loading))
    : !loading && localRows.length === 0;
  return (
    <Box direction="column" fill="flex" overflow="hidden">
      {/* The providers are renderless and stay mounted in EVERY state, empty
          included. `isEmpty` is DERIVED from what they report, so dropping them
          on the empty branch latches it: a later `query.where` would have
          nothing subscribed left to answer it, and the table would stay on the
          previous query's emptiness for the life of the mount. */}
      {isQueryMode && !queryDimension ? (
        <GroupQueryProvider<IssueRow>
          groupKey={ALL_GROUP_KEY}
          document={IssuesDocument}
          variables={{ where: query.where, orderBy: query.orderBy, limit: PAGE_LIMIT }}
          getConnection={getIssuesConnection}
          onStateChange={registerState}
        />
      ) : null}
      {isQueryMode && queryDimension
        ? (queryGroupDefs ?? []).map((def) => (
            <GroupQueryProvider<IssueRow>
              key={def.key}
              groupKey={def.key}
              document={IssuesDocument}
              variables={{ where: def.where, orderBy: query.orderBy, limit: PAGE_LIMIT }}
              getConnection={getIssuesConnection}
              onStateChange={registerState}
            />
          ))
        : null}
      {!isQueryMode
        ? buckets.map((bucket) => (
            <LocalGroupProvider<IssueRow>
              key={bucket.key}
              groupKey={bucket.key}
              rows={bucket.data}
              contextKey={groupContext}
              onStateChange={registerState}
            />
          ))
        : null}
      {isEmpty && emptyBands ? (
        <Box direction="column" paddingX="md">
          <WidgetEmptyBands action={emptyAction} bands={emptyBands} note={emptyNote} />
        </Box>
      ) : isEmpty ? (
        <WidgetEmpty
          title={emptyTitle}
          description={emptyDescription}
          action={emptyAction}
        />
      ) : (
        <Box direction="column" fill="flex" overflow="hidden">
          <DataTable
            // The table is inside a widget card, so its opaque parts — sticky
            // header, pinned cells, row washes — paint whatever the card
            // painted, not the page.
            surface={widgetSurface}
            columns={columns}
            groups={groups}
            getRowId={(row) => row.issueId}
            loading={tableLoading}
            stickyHeader
            // The card's rows dissolve at the bottom edge while more continue
            // — the same fade every other widget body wears — and the last
            // row can scroll clear of the edge.
            scrollFade
            gutterBottom={SCROLL_END_GUTTER}
            density="regular"
            groupTitle={groupTitleDimension ? renderGroupTitle : undefined}
            // The card body is full-bleed (`body: "bleed"`), so restore the
            // content inset here: row backgrounds, dividers, and the selection
            // wash reach the card edge while cell text stays padded.
            paddingX="md"
            paddingXAlign="content"
            bulkActions={selectionOn ? bulkActions : undefined}
            bulkSelectionNoun={PROJECT_SELECTION_NOUN}
            canSelectRow={
              selectionOn ? (issue) => issue.viewerCanEdit : undefined
            }
            // On touch a row is a tap to open and nothing else, as in the
            // list view: the project view carries the row's actions.
            pressOnlyOnTouch
          />
        </Box>
      )}
      {ageDialogIssue && (
        <UpdateAgeDialog
          via="widget_table"
          open
          onOpenChange={(open) => {
            if (!open) setAgeDialogIssue(null);
          }}
          issueId={ageDialogIssue.issueId}
          age={ageDialogIssue.age}
          stageId={ageDialogIssue.stageId}
        />
      )}
    </Box>
  );
}
